import { create } from 'zustand';

import { getObjectDefinition, type ObjectDefinition } from '../../content/registry/objectRegistry';
import type {
  MapDocument,
  MapFloorLevel,
  MapObjectInstance,
  MapLight,
  MapTriggerVolume,
  MapDecal,
  MapRoomMarker,
  MapCharacterInstance,
} from '../../content/schema/map';
import { migrateMapDocument } from '../../content/schema/map';
import {
  getActiveProjectId,
  getMapPersistence,
  type SaveOptions,
} from '../persistence/ProjectPersistence';
import {
  coerceParamValue,
  resolveParamSchema,
  validateInstances,
  type ParamIssue,
} from '../validation/paramValidation';
import { lintMap, type LintIssue } from '../validation/mapLinter';
import { loadEditorPreferences, saveEditorPreferences } from '../persistence/editorPreferences';

type EditorInteractionMode = 'walk' | 'edit';
// 'autosaved' is distinct from 'saved': it means the transient backup slot
// was written, not the canonical source - "discard changes" still discards
// it. Kept separate so the UI can be honest about which one just happened
// instead of showing "Saved" either way.
type SaveState = 'idle' | 'saving' | 'saved' | 'autosaved' | 'error';
export type SnapMode = 'grid' | 'off';
export type AxisLock = 'x' | 'z' | null;

const UNDO_LIMIT = 64;

interface PlacementPreview {
  floor: MapFloorLevel;
  position: [number, number, number];
}

export interface FocusTarget {
  /** Instance id that triggered the focus - used for toast/highlight. */
  instanceId: string;
  /** Floor we need to end up on. */
  floor: MapFloorLevel;
  /** Player/camera approach position in world space. */
  approach: [number, number, number];
  /** Point the camera should look at. */
  lookAt: [number, number, number];
  /** Whether the initial transition should teleport (cross-floor) before panning. */
  instant: boolean;
}

interface SnapshotData {
  instances: MapObjectInstance[];
  lights: MapLight[];
  triggers: MapTriggerVolume[];
  decals: MapDecal[];
  markers: MapRoomMarker[];
}

interface UndoEntry {
  snapshot: SnapshotData;
  label: string;
}

export interface InstanceDiff {
  added: MapObjectInstance[];
  removed: MapObjectInstance[];
  modified: Array<{
    before: MapObjectInstance;
    after: MapObjectInstance;
    changes: Array<'position' | 'rotation' | 'params' | 'tags' | 'floor'>;
  }>;
}

/** Partial update touching one of the per-entity-kind arrays in the store. */
type EntityArraysPatch = Partial<
  Pick<MapEditorState, 'objectInstances' | 'lights' | 'triggers' | 'decals' | 'markers'>
>;

interface MapEditorState {
  /** The full MapDocument hydrated in the editor. */
  activeDocument: MapDocument | null;
  enabled: boolean;
  interactionMode: EditorInteractionMode;
  snapMode: SnapMode;
  axisLock: AxisLock;
  searchQuery: string;
  selectedObjectIds: string[];
  hoveredObjectId: string | null;
  pendingAssetId: string | null;
  placementRotationY: number;
  placementPreview: PlacementPreview | null;
  objectInstances: MapObjectInstance[];
  sourceInstances: MapObjectInstance[];

  // Non-prop collections
  lights: MapLight[];
  sourceLights: MapLight[];
  triggers: MapTriggerVolume[];
  sourceTriggers: MapTriggerVolume[];
  decals: MapDecal[];
  sourceDecals: MapDecal[];
  markers: MapRoomMarker[];
  sourceMarkers: MapRoomMarker[];

  // Placed characters (3D). Additive alongside the five collections above:
  // selection (`selectedObjectIds`/`hoveredObjectId`) and the gizmo-driven
  // position/facing edit in LevelRenderer's SceneCharacterSelection both
  // work off this live array the same way they do for props/lights/etc, but
  // it isn't (yet) plumbed into getEntityCollection, so undo/redo, nudge,
  // duplicate and delete don't recognize a character id - only Studio's
  // Characters-mode placement panel and the viewport gizmo write here.
  characterInstances: MapCharacterInstance[];
  sourceCharacterInstances: MapCharacterInstance[];

  // Visibility layers
  visibilityToggles: {
    props: boolean;
    lights: boolean;
    triggers: boolean;
    markers: boolean;
    decals: boolean;
  };
  setVisibilityToggle: (category: 'props' | 'lights' | 'triggers' | 'markers' | 'decals', visible: boolean) => void;

  // Playtest in place
  playtesting: boolean;
  playtestSpawnPosition: [number, number, number] | null;
  playtestSpawnFloor: number | null;
  savedPlayerState: {
    position: [number, number, number];
    yaw: number;
    pitch: number;
    floor: number;
  } | null;
  startPlaytest: (spawnPos: [number, number, number], floor: number, yaw?: number) => void;
  exitPlaytest: () => void;
  playerPosition: [number, number, number] | null;
  playerYaw: number;

  // Collision state

  undoStack: UndoEntry[];
  redoStack: UndoEntry[];
  saveState: SaveState;
  statusMessage: string | null;
  autoSaveEnabled: boolean;
  activeRightPanel: 'inspector' | 'list' | 'prefabs' | 'history';
  firstSelectionTipDismissed: boolean;
  lastValidationIssues: ParamIssue[];
  /** Structural linter issues from the most recent save/diff (see lintMap). */
  lastLintIssues: LintIssue[];
  focusTarget: FocusTarget | null;
  /** Floors available in the currently-loaded map document. */
  availableFloors: number[];
  /** The single instance held on the clipboard (Ctrl+C). */
  clipboardInstance: MapObjectInstance | MapLight | MapTriggerVolume | MapDecal | MapRoomMarker | null;
  /** Active gizmo transform mode, or 'off'. */
  gizmoMode: 'translate' | 'rotate' | 'scale' | 'off';
  /** Whether the editor is in free-camera (fly) mode. */
  freeCamMode: boolean;
  /** Current marquee drag rectangle in screen pixels, or null when inactive. */
  marqueeRect: { x: number; y: number; w: number; h: number } | null;
  /** Last raycast hit XZ position (world space) used for paste placement. */
  lastRaycastHit: [number, number] | null;
  /** Projection helper from world coordinates to screen coordinates. */
  projectToScreen: ((worldPos: [number, number, number]) => [number, number] | null) | null;
  setProjectToScreen: (fn: ((worldPos: [number, number, number]) => [number, number] | null) | null) => void;

  // Hydration
  hydrateFromDocument: (document: MapDocument) => void;

  // Mode
  setEnabled: (enabled: boolean) => void;
  toggleEnabled: () => void;
  setInteractionMode: (mode: EditorInteractionMode) => void;
  setSnapMode: (mode: SnapMode) => void;
  setAxisLock: (axis: AxisLock) => void;

  // Floor
  setAvailableFloors: (floors: number[]) => void;

  // Search / palette
  setSearchQuery: (query: string) => void;
  setPendingAssetId: (assetId: string | null) => void;
  setPlacementPreview: (preview: PlacementPreview | null) => void;
  rotatePlacementBy: (deltaY: number) => void;
  resetPlacementRotation: () => void;

  // Selection
  selectObject: (id: string | null) => void;
  toggleSelectObject: (id: string) => void;
  setSelectedObjectIds: (ids: string[]) => void;
  selectAll: (floor: MapFloorLevel) => void;
  clearSelection: () => void;
  setHoveredObjectId: (id: string | null) => void;

  // Right panel
  setActiveRightPanel: (panel: 'inspector' | 'list' | 'prefabs' | 'history') => void;
  dismissFirstSelectionTip: () => void;

  // Clipboard (Ctrl+C / Ctrl+V)
  copySelected: () => void;
  pasteAtPosition: (x: number, z: number, floor: MapFloorLevel) => string | null;
  setLastRaycastHit: (pos: [number, number] | null) => void;

  // Gizmo
  setGizmoMode: (mode: 'translate' | 'rotate' | 'scale' | 'off') => void;

  // Free-cam
  setFreeCamMode: (enabled: boolean) => void;

  // Marquee
  setMarqueeRect: (rect: { x: number; y: number; w: number; h: number } | null) => void;

  // Mutations
  updateObject: (id: string, updater: (object: MapObjectInstance) => MapObjectInstance, label?: string) => void;
  moveObject: (id: string, position: [number, number, number], label?: string) => void;
  moveObjectImmediate: (id: string, position: [number, number, number]) => void;
  moveGroupBy: (ids: string[], delta: [number, number, number], label?: string) => void;
  moveGroupImmediate: (ids: string[], delta: [number, number, number]) => void;
  nudgeSelected: (delta: [number, number, number]) => void;
  beginDrag: (id: string | string[], label?: string) => void;
  rotateObject: (id: string, deltaY: number, label?: string) => void;
  updateObjectParam: (id: string, key: string, value: unknown) => void;
  addObject: (assetId: string, floor: MapFloorLevel, position: [number, number, number], rotationY?: number) => string | null;
  /**
   * Insert a fully-constructed `MapObjectInstance` directly (used by paste and
   * prefab drop). The instance's id must already be unique; use `makeObjectId`
   * to generate one. The caller is responsible for validating the assetId.
   */
  insertInstance: (instance: MapObjectInstance, label?: string) => void;
  /**
   * Insert a whole prefab group (props, lights, triggers, decals) in one undo
   * step, each source item's position offset by `offset` (XZ) and placed on
   * `floor`. Every inserted item gets a freshly generated id. Returns the new
   * ids across all kinds, in the order instances/lights/triggers/decals.
   */
  insertPrefabGroup: (
    group: {
      instances?: MapObjectInstance[];
      lights?: MapLight[];
      triggers?: MapTriggerVolume[];
      decals?: MapDecal[];
    },
    floor: MapFloorLevel,
    offset: [number, number],
    label?: string,
  ) => string[];
  duplicateObject: (id: string) => string | null;
  duplicateWithOffset: (id: string, count: number, offset: [number, number]) => string[];
  deleteObject: (id: string) => void;
  deleteSelected: () => void;

  // Undo / redo
  undo: () => void;
  redo: () => void;

  // Persistence
  resetToSource: () => void;
  restoreFromSnapshot: (instances: MapObjectInstance[], label: string) => void;
  loadFromDisk: () => Promise<void>;
  saveToDisk: (options?: SaveOptions) => Promise<ParamIssue[] | undefined>;
  loadGitHeadSnapshot: () => Promise<MapObjectInstance[] | null>;
  setAutoSaveEnabled: (enabled: boolean) => void;
  clearStatus: () => void;
  validateAll: () => ParamIssue[];

  // Focus / camera
  focusOnInstance: (id: string, options?: { currentFloor?: MapFloorLevel }) => void;
  clearFocusTarget: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Create a unique, human-readable instance id from an assetId prefix.
 * Exported so tools like PrefabLibrary can generate ids client-side.
 */
export function makeObjectId(assetId: string): string {
  const slug = assetId.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 24);
  return `${slug}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function cloneInstance(instance: MapObjectInstance): MapObjectInstance {
  return {
    ...instance,
    transform: {
      position: [...instance.transform.position] as [number, number, number],
      rotation: instance.transform.rotation ? [...instance.transform.rotation] as [number, number, number] : undefined,
      scale: instance.transform.scale ? [...instance.transform.scale] as [number, number, number] : undefined,
    },
    params: instance.params ? { ...instance.params } : undefined,
    tags: instance.tags ? [...instance.tags] : undefined,
  };
}

function cloneInstances(instances: MapObjectInstance[]): MapObjectInstance[] {
  return instances.map(cloneInstance);
}

function cloneLight(light: MapLight): MapLight {
  return {
    ...light,
    position: [...light.position] as [number, number, number],
    params: light.params ? { ...light.params } : undefined,
  };
}

function cloneTrigger(trigger: MapTriggerVolume): MapTriggerVolume {
  return {
    ...trigger,
    center: [...trigger.center] as [number, number],
    size: [...trigger.size] as [number, number],
    targetPosition: trigger.targetPosition ? [...trigger.targetPosition] as [number, number] : undefined,
    params: trigger.params ? { ...trigger.params } : undefined,
  };
}

function cloneDecal(decal: MapDecal): MapDecal {
  return {
    ...decal,
    position: [...decal.position] as [number, number, number],
    rotation: decal.rotation ? [...decal.rotation] as [number, number, number] : undefined,
    size: [...decal.size] as [number, number],
    params: decal.params ? { ...decal.params } : undefined,
  };
}

function cloneMarker(marker: MapRoomMarker): MapRoomMarker {
  return {
    ...marker,
    position: [...marker.position] as [number, number],
    metadata: marker.metadata ? { ...marker.metadata } : undefined,
  };
}

function cloneCharacterInstance(instance: MapCharacterInstance): MapCharacterInstance {
  return {
    ...instance,
    position: [...instance.position] as [number, number, number],
    patrol: instance.patrol
      ? { ...instance.patrol, points: instance.patrol.points.map((p) => ({ ...p })) }
      : undefined,
  };
}

function cloneSnapshotData(data: SnapshotData): SnapshotData {
  return {
    instances: cloneInstances(data.instances),
    lights: data.lights.map(cloneLight),
    triggers: data.triggers.map(cloneTrigger),
    decals: data.decals.map(cloneDecal),
    markers: data.markers.map(cloneMarker),
  };
}

function getSnapshotData(state: MapEditorState): SnapshotData {
  return {
    instances: state.objectInstances,
    lights: state.lights,
    triggers: state.triggers,
    decals: state.decals,
    markers: state.markers,
  };
}

export function getEntityCollection(state: MapEditorState, id: string): 'objectInstances' | 'lights' | 'triggers' | 'decals' | 'markers' | null {
  if (state.objectInstances.some(i => i.id === id)) return 'objectInstances';
  if (state.lights.some(i => i.id === id)) return 'lights';
  if (state.triggers.some(i => i.id === id)) return 'triggers';
  if (state.decals.some(i => i.id === id)) return 'decals';
  if (state.markers.some(i => i.id === id)) return 'markers';
  return null;
}

export function getEntityPosition(state: MapEditorState, id: string): [number, number, number] | null {
  const col = getEntityCollection(state, id);
  if (col === 'objectInstances') return state.objectInstances.find(i => i.id === id)!.transform.position;
  if (col === 'lights') return state.lights.find(l => l.id === id)!.position;
  if (col === 'decals') return state.decals.find(d => d.id === id)!.position;
  if (col === 'triggers') {
    const t = state.triggers.find(t => t.id === id)!;
    return [t.center[0], 0, t.center[1]];
  }
  if (col === 'markers') {
    const m = state.markers.find(m => m.id === id)!;
    return [m.position[0], 0, m.position[1]];
  }
  return null;
}

export function moveEntityInternal(state: MapEditorState, id: string, nextPos: [number, number, number], snapMode: SnapMode) {
  const collection = getEntityCollection(state, id);
  if (!collection) return {};

  if (collection === 'objectInstances') {
    const inst = state.objectInstances.find(i => i.id === id);
    if (!inst) return {};
    const snapped = snapPosition(inst.assetId, nextPos, snapMode);
    return {
      objectInstances: state.objectInstances.map(i => i.id === id ? { ...i, transform: { ...i.transform, position: snapped } } : i),
    };
  }
  if (collection === 'lights') {
    const snapped: [number, number, number] = [
      Number((snapMode === 'grid' ? Math.round(nextPos[0] / 0.1) * 0.1 : nextPos[0]).toFixed(3)),
      Number(nextPos[1].toFixed(3)),
      Number((snapMode === 'grid' ? Math.round(nextPos[2] / 0.1) * 0.1 : nextPos[2]).toFixed(3)),
    ];
    return {
      lights: state.lights.map(l => l.id === id ? { ...l, position: snapped } : l),
    };
  }
  if (collection === 'decals') {
    const snapped: [number, number, number] = [
      Number((snapMode === 'grid' ? Math.round(nextPos[0] / 0.05) * 0.05 : nextPos[0]).toFixed(3)),
      Number(nextPos[1].toFixed(3)),
      Number((snapMode === 'grid' ? Math.round(nextPos[2] / 0.05) * 0.05 : nextPos[2]).toFixed(3)),
    ];
    return {
      decals: state.decals.map(d => d.id === id ? { ...d, position: snapped } : d),
    };
  }
  if (collection === 'triggers') {
    const snapped: [number, number] = [
      Number((snapMode === 'grid' ? Math.round(nextPos[0] / 0.1) * 0.1 : nextPos[0]).toFixed(3)),
      Number((snapMode === 'grid' ? Math.round(nextPos[2] / 0.1) * 0.1 : nextPos[2]).toFixed(3)),
    ];
    return {
      triggers: state.triggers.map(t => t.id === id ? { ...t, center: snapped } : t),
    };
  }
  if (collection === 'markers') {
    const snapped: [number, number] = [
      Number((snapMode === 'grid' ? Math.round(nextPos[0] / 0.1) * 0.1 : nextPos[0]).toFixed(3)),
      Number((snapMode === 'grid' ? Math.round(nextPos[2] / 0.1) * 0.1 : nextPos[2]).toFixed(3)),
    ];
    return {
      markers: state.markers.map(m => m.id === id ? { ...m, position: snapped } : m),
    };
  }
  return {};
}

export function rotateEntityInternal(state: MapEditorState, id: string, deltaY: number) {
  const collection = getEntityCollection(state, id);
  if (!collection) return {};

  if (collection === 'objectInstances') {
    return {
      objectInstances: state.objectInstances.map(i => {
        if (i.id !== id) return i;
        const rot = i.transform.rotation ?? [0, 0, 0];
        return {
          ...i,
          transform: {
            ...i.transform,
            rotation: [rot[0], Number((rot[1] + deltaY).toFixed(3)), rot[2]] as [number, number, number],
          },
        };
      }),
    };
  }
  if (collection === 'decals') {
    return {
      decals: state.decals.map(d => {
        if (d.id !== id) return d;
        const rot = d.rotation ?? [0, 0, 0];
        return {
          ...d,
          rotation: [rot[0], Number((rot[1] + deltaY).toFixed(3)), rot[2]] as [number, number, number],
        };
      }),
    };
  }
  return {};
}

export function updateEntityParamInternal(state: MapEditorState, id: string, key: string, value: unknown) {
  const collection = getEntityCollection(state, id);
  if (!collection) return {};

  if (collection === 'objectInstances') {
    const inst = state.objectInstances.find(i => i.id === id);
    if (!inst) return {};
    const schema = resolveParamSchema(inst.assetId, key);
    let nextVal = value;
    if (schema) {
      const coerced = coerceParamValue(schema, value);
      if (coerced !== null) nextVal = coerced;
    }
    return {
      objectInstances: state.objectInstances.map(i => i.id === id ? { ...i, params: { ...(i.params ?? {}), [key]: nextVal } } : i),
    };
  }

  if (collection === 'lights') {
    return {
      lights: state.lights.map(l => {
        if (l.id !== id) return l;
        if (['color', 'intensity', 'range', 'angle', 'castShadow', 'kind'].includes(key)) {
          return { ...l, [key]: value };
        }
        return { ...l, params: { ...(l.params ?? {}), [key]: value } };
      }),
    };
  }
  if (collection === 'triggers') {
    return {
      triggers: state.triggers.map(t => {
        if (t.id !== id) return t;
        if (['kind', 'targetFloor', 'eventName', 'height'].includes(key)) {
          return { ...t, [key]: value };
        }
        if (key === 'targetPosition') {
          return { ...t, targetPosition: value as [number, number] };
        }
        if (key === 'size') {
          return { ...t, size: value as [number, number] };
        }
        return { ...t, params: { ...(t.params ?? {}), [key]: value } };
      }),
    };
  }
  if (collection === 'decals') {
    return {
      decals: state.decals.map(d => {
        if (d.id !== id) return d;
        if (['material', 'opacity'].includes(key)) {
          return { ...d, [key]: value };
        }
        if (key === 'size') {
          return { ...d, size: value as [number, number] };
        }
        if (key === 'rotation') {
          return { ...d, rotation: value as [number, number, number] };
        }
        return { ...d, params: { ...(d.params ?? {}), [key]: value } };
      }),
    };
  }
  if (collection === 'markers') {
    return {
      markers: state.markers.map(m => {
        if (m.id !== id) return m;
        if (['name', 'kind'].includes(key)) {
          return { ...m, [key]: value };
        }
        return { ...m, metadata: { ...(m.metadata ?? {}), [key]: value } };
      }),
    };
  }

  return {};
}

export function deleteEntityInternal(state: MapEditorState, id: string) {
  const collection = getEntityCollection(state, id);
  if (!collection) return {};

  return {
    objectInstances: state.objectInstances.filter(i => i.id !== id),
    lights: state.lights.filter(l => l.id !== id),
    triggers: state.triggers.filter(t => t.id !== id),
    decals: state.decals.filter(d => d.id !== id),
    markers: state.markers.filter(m => m.id !== id),
    selectedObjectIds: state.selectedObjectIds.filter(s => s !== id),
  };
}

function roundToStep(value: number, step: number) {
  return Math.round(value / step) * step;
}

/** Derive a placeable light's kind/color/intensity/range/angle from its
 * asset id, the registry's own defaults for it, and any per-placement
 * overrides (a prefab/preset instance's own authored color/intensity).
 * Shared by `addObject` (live click-to-place) and Studio's preset-drop path
 * (editor-panels.tsx's DockPrefabs) so both build the same light shape from
 * the same rules instead of two hand-copied, driftable versions. */
export function lightFieldsFromAssetId(
  assetId: string,
  definition: ObjectDefinition | null,
  overrides?: { color?: string; intensity?: number },
): Pick<MapLight, 'kind' | 'color' | 'intensity' | 'range' | 'angle' | 'castShadow'> {
  const kind: MapLight['kind'] = assetId.includes('spot') ? 'spot' : assetId.includes('ambient') ? 'ambient-override' : 'point';
  return {
    kind,
    color: overrides?.color ?? (definition?.defaultParams?.color as string | undefined) ?? '#ffffff',
    intensity: overrides?.intensity ?? (definition?.defaultParams?.intensity as number | undefined) ?? 1.0,
    range: (definition?.defaultParams?.range as number | undefined) ?? 8,
    angle: (definition?.defaultParams?.angle as number | undefined) ?? 0.5,
    castShadow: true,
  };
}

function snapPosition(assetId: string, position: [number, number, number], snapMode: SnapMode): [number, number, number] {
  if (snapMode === 'off') {
    return [
      Number(position[0].toFixed(3)),
      Number(position[1].toFixed(3)),
      Number(position[2].toFixed(3)),
    ];
  }
  const definition = getObjectDefinition(assetId);
  const step = definition?.editor.snap ?? 0.1;
  return [
    Number(roundToStep(position[0], step).toFixed(3)),
    Number(position[1].toFixed(3)),
    Number(roundToStep(position[2], step).toFixed(3)),
  ];
}

function pushUndoPatch(
  state: { undoStack: UndoEntry[]; redoStack: UndoEntry[] },
  snapshot: SnapshotData,
  label: string,
): { undoStack: UndoEntry[]; redoStack: UndoEntry[] } {
  const entry: UndoEntry = { snapshot: cloneSnapshotData(snapshot), label };
  return {
    undoStack: [entry, ...state.undoStack].slice(0, UNDO_LIMIT),
    redoStack: [],
  };
}

function shallowEqualPosition(a: [number, number, number], b: [number, number, number]) {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function shallowEqualParams(a?: Record<string, unknown>, b?: Record<string, unknown>) {
  if (a === b) return true;
  const ka = a ? Object.keys(a) : [];
  const kb = b ? Object.keys(b) : [];
  if (ka.length !== kb.length) return false;
  for (const key of ka) {
    if (!b || !Object.is(a?.[key], b[key])) return false;
  }
  return true;
}

function shallowEqualTags(a?: string[], b?: string[]) {
  if (a === b) return true;
  if ((a?.length ?? 0) !== (b?.length ?? 0)) return false;
  for (let i = 0; i < (a?.length ?? 0); i += 1) {
    if (a?.[i] !== b?.[i]) return false;
  }
  return true;
}

function instancesEqual(a: MapObjectInstance, b: MapObjectInstance) {
  if (a.id !== b.id) return false;
  if (a.assetId !== b.assetId) return false;
  if (a.floor !== b.floor) return false;
  if (!shallowEqualPosition(a.transform.position, b.transform.position)) return false;
  const ar = a.transform.rotation ?? [0, 0, 0];
  const br = b.transform.rotation ?? [0, 0, 0];
  if (ar[0] !== br[0] || ar[1] !== br[1] || ar[2] !== br[2]) return false;
  if (!shallowEqualParams(a.params, b.params)) return false;
  if (!shallowEqualTags(a.tags, b.tags)) return false;
  return true;
}

export function diffInstances(before: MapObjectInstance[], after: MapObjectInstance[]): InstanceDiff {
  const beforeById = new Map(before.map((instance) => [instance.id, instance]));
  const afterById = new Map(after.map((instance) => [instance.id, instance]));
  const added: MapObjectInstance[] = [];
  const removed: MapObjectInstance[] = [];
  const modified: InstanceDiff['modified'] = [];

  for (const [id, a] of afterById) {
    const b = beforeById.get(id);
    if (!b) {
      added.push(a);
      continue;
    }
    if (!instancesEqual(a, b)) {
      const changes: InstanceDiff['modified'][number]['changes'] = [];
      if (!shallowEqualPosition(a.transform.position, b.transform.position)) changes.push('position');
      const ar = a.transform.rotation ?? [0, 0, 0];
      const br = b.transform.rotation ?? [0, 0, 0];
      if (ar[0] !== br[0] || ar[1] !== br[1] || ar[2] !== br[2]) changes.push('rotation');
      if (!shallowEqualParams(a.params, b.params)) changes.push('params');
      if (!shallowEqualTags(a.tags, b.tags)) changes.push('tags');
      if (a.floor !== b.floor) changes.push('floor');
      modified.push({ before: b, after: a, changes });
    }
  }

  for (const [id, b] of beforeById) {
    if (!afterById.has(id)) {
      removed.push(b);
    }
  }

  return { added, removed, modified };
}

export function isDirty(
  objectInstances: MapObjectInstance[],
  sourceInstances: MapObjectInstance[],
  storeState?: {
    lights: MapLight[];
    sourceLights: MapLight[];
    triggers: MapTriggerVolume[];
    sourceTriggers: MapTriggerVolume[];
    decals: MapDecal[];
    sourceDecals: MapDecal[];
    markers: MapRoomMarker[];
    sourceMarkers: MapRoomMarker[];
  }
): boolean {
  if (objectInstances.length !== sourceInstances.length) return true;
  const sourceById = new Map(sourceInstances.map((instance) => [instance.id, instance]));
  for (const instance of objectInstances) {
    const source = sourceById.get(instance.id);
    if (!source || !instancesEqual(instance, source)) return true;
  }

  if (storeState) {
    if (storeState.lights.length !== storeState.sourceLights.length) return true;
    if (storeState.triggers.length !== storeState.sourceTriggers.length) return true;
    if (storeState.decals.length !== storeState.sourceDecals.length) return true;
    if (storeState.markers.length !== storeState.sourceMarkers.length) return true;

    const lightsById = new Map(storeState.sourceLights.map(l => [l.id, l]));
    for (const l of storeState.lights) {
      const sl = lightsById.get(l.id);
      if (!sl) return true;
      if (l.floor !== sl.floor || l.kind !== sl.kind || l.color !== sl.color || l.intensity !== sl.intensity || l.range !== sl.range || l.angle !== sl.angle || l.castShadow !== sl.castShadow) return true;
      if (l.position[0] !== sl.position[0] || l.position[1] !== sl.position[1] || l.position[2] !== sl.position[2]) return true;
      if (!shallowEqualParams(l.params, sl.params)) return true;
    }

    const triggersById = new Map(storeState.sourceTriggers.map(t => [t.id, t]));
    for (const t of storeState.triggers) {
      const st = triggersById.get(t.id);
      if (!st) return true;
      if (t.floor !== st.floor || t.kind !== st.kind || t.height !== st.height || t.targetFloor !== st.targetFloor || t.eventName !== st.eventName) return true;
      if (t.targetPosition?.[0] !== st.targetPosition?.[0] || t.targetPosition?.[1] !== st.targetPosition?.[1]) return true;
      if (t.center[0] !== st.center[0] || t.center[1] !== st.center[1]) return true;
      if (t.size[0] !== st.size[0] || t.size[1] !== st.size[1]) return true;
      // Bomb-site assignment lives in params.site - compare it so a site-only
      // edit still marks the document dirty.
      if (!shallowEqualParams(t.params, st.params)) return true;
    }

    const decalsById = new Map(storeState.sourceDecals.map(d => [d.id, d]));
    for (const d of storeState.decals) {
      const sd = decalsById.get(d.id);
      if (!sd) return true;
      if (d.floor !== sd.floor || d.material !== sd.material || d.opacity !== sd.opacity) return true;
      if (d.position[0] !== sd.position[0] || d.position[1] !== sd.position[1] || d.position[2] !== sd.position[2]) return true;
      if (!shallowEqualParams(d.params, sd.params)) return true;
    }

    const markersById = new Map(storeState.sourceMarkers.map(m => [m.id, m]));
    for (const m of storeState.markers) {
      const sm = markersById.get(m.id);
      if (!sm) return true;
      if (m.floor !== sm.floor || m.name !== sm.name || m.kind !== sm.kind) return true;
      if (m.position[0] !== sm.position[0] || m.position[1] !== sm.position[1]) return true;
      if (!shallowEqualParams(m.metadata, sm.metadata)) return true;
    }
  }

  return false;
}

/** Fields `isStoreDirty` inspects - every working array and its saved source. */
type DirtyStoreState = {
  objectInstances: MapObjectInstance[];
  sourceInstances: MapObjectInstance[];
  lights: MapLight[];
  sourceLights: MapLight[];
  triggers: MapTriggerVolume[];
  sourceTriggers: MapTriggerVolume[];
  decals: MapDecal[];
  sourceDecals: MapDecal[];
  markers: MapRoomMarker[];
  sourceMarkers: MapRoomMarker[];
};

/**
 * Whole-document dirty check over EVERY working collection (props, lights,
 * triggers, decals and markers), not just props. Use this for autosave, the
 * beforeunload guard and the unsaved indicator so a spawn marker's team or a
 * bomb-site trigger's params - data the game consumes - are never silently lost
 * on tab close. The bare two-arg `isDirty` only sees props and must not be used
 * for dirty tracking on Sunder maps.
 */
export function isStoreDirty(state: DirtyStoreState): boolean {
  return isDirty(state.objectInstances, state.sourceInstances, state);
}

/**
 * Assemble a MapDocument from the store's LIVE arrays (markers/triggers/lights/
 * decals/objectInstances) layered over the hydrated document. `activeDocument`
 * is frozen at hydration, so linting or exporting must read the live arrays or
 * it validates a stale snapshot that predates the author's edits.
 */
export function buildLiveMapDocument(state: {
  activeDocument: MapDocument | null;
  objectInstances: MapObjectInstance[];
  lights: MapLight[];
  triggers: MapTriggerVolume[];
  decals: MapDecal[];
  markers: MapRoomMarker[];
}): MapDocument | null {
  if (!state.activeDocument) return null;
  return {
    ...state.activeDocument,
    objectInstances: state.objectInstances,
    lights: state.lights,
    triggers: state.triggers,
    decals: state.decals,
    markers: state.markers,
  };
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useMapEditorStore = create<MapEditorState>((set, get) => ({
  enabled: false,
  interactionMode: 'walk',
  snapMode: loadEditorPreferences().snapMode ?? 'grid',
  axisLock: null,
  searchQuery: '',
  selectedObjectIds: [],
  hoveredObjectId: null,
  pendingAssetId: null,
  placementRotationY: 0,
  placementPreview: null,
  objectInstances: [],
  sourceInstances: [],

  lights: [],
  sourceLights: [],
  triggers: [],
  sourceTriggers: [],
  decals: [],
  sourceDecals: [],
  markers: [],
  sourceMarkers: [],

  characterInstances: [],
  sourceCharacterInstances: [],

  visibilityToggles: {
    props: true,
    lights: true,
    triggers: true,
    markers: true,
    decals: true,
  },
  setVisibilityToggle: (category, visible) => {
    set((state) => ({
      visibilityToggles: {
        ...state.visibilityToggles,
        [category]: visible,
      },
    }));
  },

  playtesting: false,
  playtestSpawnPosition: null,
  playtestSpawnFloor: null,
  savedPlayerState: null,
  playerPosition: null,
  playerYaw: 0,

  startPlaytest: (spawnPos, floor, yaw = 0) => {
    // Default fallback to save current camera / freecam position
    set({
      playtesting: true,
      playtestSpawnPosition: spawnPos,
      playtestSpawnFloor: floor,
      savedPlayerState: {
        position: spawnPos,
        yaw: yaw,
        pitch: -0.08,
        floor: floor,
      },
      interactionMode: 'walk',
      freeCamMode: false,
    });
  },

  exitPlaytest: () => {
    set({
      playtesting: false,
      playtestSpawnPosition: null,
      playtestSpawnFloor: null,
      interactionMode: 'edit',
    });
  },


  undoStack: [],
  redoStack: [],
  saveState: 'idle',
  statusMessage: null,
  autoSaveEnabled: loadEditorPreferences().autoSaveEnabled ?? false,
  activeRightPanel: loadEditorPreferences().activeRightPanel ?? 'inspector',
  firstSelectionTipDismissed: false,
  lastValidationIssues: [],
  lastLintIssues: [],
  focusTarget: null,
  availableFloors: [],
  clipboardInstance: null,
  gizmoMode: 'off',
  freeCamMode: false,
  marqueeRect: null,
  lastRaycastHit: null,
  activeDocument: null,
  projectToScreen: null,

  hydrateFromDocument: (incoming) => {
    // Upgrade saved maps to the current schema on load: legacy
    // `interactiveObjects` become registry-driven triggers, so the editor never
    // hydrates - nor writes back - a deprecated field.
    const document = migrateMapDocument(incoming);
    const cloned = cloneInstances(document.objectInstances);
    const docLights = document.lights ? document.lights.map(cloneLight) : [];
    const docTriggers = document.triggers ? document.triggers.map(cloneTrigger) : [];
    const docDecals = document.decals ? document.decals.map(cloneDecal) : [];
    const docMarkers = document.markers ? document.markers.map(cloneMarker) : [];
    const docCharacterInstances = document.characterInstances ? document.characterInstances.map(cloneCharacterInstance) : [];


    set({
      activeDocument: document,
      objectInstances: cloned,
      sourceInstances: cloneInstances(document.objectInstances),
      lights: docLights,
      sourceLights: document.lights ? document.lights.map(cloneLight) : [],
      triggers: docTriggers,
      sourceTriggers: document.triggers ? document.triggers.map(cloneTrigger) : [],
      decals: docDecals,
      sourceDecals: document.decals ? document.decals.map(cloneDecal) : [],
      markers: docMarkers,
      sourceMarkers: document.markers ? document.markers.map(cloneMarker) : [],
      characterInstances: docCharacterInstances,
      sourceCharacterInstances: document.characterInstances ? document.characterInstances.map(cloneCharacterInstance) : [],
      undoStack: [],
      redoStack: [],
      statusMessage: null,
      saveState: 'idle',
      lastValidationIssues: [],
      availableFloors: document.floors.map((f) => f.level),
      playtesting: false,
      playtestSpawnPosition: null,
      playtestSpawnFloor: null,
      savedPlayerState: null,
    });
  },

  setAvailableFloors: (floors) => set({ availableFloors: floors }),
  setProjectToScreen: (fn) => set({ projectToScreen: fn }),

  setEnabled: (enabled) => set((state) => ({
    enabled,
    interactionMode: enabled ? state.interactionMode : 'walk',
    pendingAssetId: enabled ? state.pendingAssetId : null,
    placementPreview: enabled ? state.placementPreview : null,
    selectedObjectIds: enabled ? state.selectedObjectIds : [],
    hoveredObjectId: enabled ? state.hoveredObjectId : null,
    axisLock: enabled ? state.axisLock : null,
  })),

  toggleEnabled: () => {
    const state = get();
    get().setEnabled(!state.enabled);
  },

  setInteractionMode: (mode) => set({
    interactionMode: mode,
    placementPreview: mode === 'edit' ? get().placementPreview : null,
  }),

  setSnapMode: (mode) => {
    set({ snapMode: mode });
    saveEditorPreferences({
      snapMode: mode,
      autoSaveEnabled: get().autoSaveEnabled,
      activeRightPanel: get().activeRightPanel,
    });
  },
  setAxisLock: (axis) => set({ axisLock: axis }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  setPendingAssetId: (assetId) => set({
    pendingAssetId: assetId,
    placementPreview: assetId ? get().placementPreview : null,
    placementRotationY: assetId ? get().placementRotationY : 0,
    interactionMode: assetId ? 'edit' : get().interactionMode,
  }),

  setPlacementPreview: (preview) => set({ placementPreview: preview }),
  rotatePlacementBy: (deltaY) => set((state) => ({
    placementRotationY: Number((state.placementRotationY + deltaY).toFixed(4)),
  })),
  resetPlacementRotation: () => set({ placementRotationY: 0 }),

  selectObject: (id) => set({
    selectedObjectIds: id ? [id] : [],
    pendingAssetId: id ? null : get().pendingAssetId,
  }),

  toggleSelectObject: (id) => set((state) => {
    const already = state.selectedObjectIds.includes(id);
    return {
      selectedObjectIds: already
        ? state.selectedObjectIds.filter((s) => s !== id)
        : [...state.selectedObjectIds, id],
      pendingAssetId: null,
    };
  }),

  setSelectedObjectIds: (ids) => set({ selectedObjectIds: [...new Set(ids)], pendingAssetId: null }),

  selectAll: (floor) => set((state) => ({
    selectedObjectIds: state.objectInstances
      .filter((instance) => instance.floor === floor)
      .map((instance) => instance.id),
    pendingAssetId: null,
  })),

  clearSelection: () => set({ selectedObjectIds: [], pendingAssetId: null }),

  setHoveredObjectId: (id) => set({ hoveredObjectId: id }),

  setActiveRightPanel: (panel) => {
    set({ activeRightPanel: panel });
    saveEditorPreferences({
      snapMode: get().snapMode,
      autoSaveEnabled: get().autoSaveEnabled,
      activeRightPanel: panel,
    });
  },
  dismissFirstSelectionTip: () => set({ firstSelectionTipDismissed: true }),

  // ── Clipboard ──────────────────────────────────────────────────────────────

  copySelected: () => {
    const state = get();
    if (state.selectedObjectIds.length !== 1) return;
    const id = state.selectedObjectIds[0]!;
    const col = getEntityCollection(state, id);
    if (!col) return;
    let copied: MapEditorState['clipboardInstance'] = null;
    if (col === 'objectInstances') copied = cloneInstance(state.objectInstances.find(i => i.id === id)!);
    else if (col === 'lights') copied = cloneLight(state.lights.find(i => i.id === id)!);
    else if (col === 'triggers') copied = cloneTrigger(state.triggers.find(i => i.id === id)!);
    else if (col === 'decals') copied = cloneDecal(state.decals.find(i => i.id === id)!);
    else if (col === 'markers') copied = cloneMarker(state.markers.find(i => i.id === id)!);
    
    if (copied) {
      set({ clipboardInstance: copied, statusMessage: `Copied ${id}.` });
    }
  },

  pasteAtPosition: (x, z, floor) => {
    const state = get();
    const src = state.clipboardInstance;
    if (!src) return null;
    
    const id = makeObjectId(src.id);
    const undoPatch = pushUndoPatch(state, getSnapshotData(state), 'Paste');

    let updatedArrays: EntityArraysPatch;
    if ('assetId' in src) {
      const definition = getObjectDefinition(src.assetId);
      if (!definition) return null;
      const position: [number, number, number] = [x, src.transform.position[1], z];
      const next: MapObjectInstance = {
        ...src,
        id,
        floor,
        transform: {
          ...src.transform,
          position: snapPosition(src.assetId, position, state.snapMode),
          rotation: src.transform.rotation ? [...src.transform.rotation] as [number, number, number] : [0, 0, 0],
          scale: src.transform.scale ? [...src.transform.scale] as [number, number, number] : [1, 1, 1],
        },
        params: src.params ? { ...src.params } : undefined,
        tags: src.tags ? [...src.tags] : undefined,
      };
      updatedArrays = {
        objectInstances: [...state.objectInstances, next],
      };
    } else if ('intensity' in src) {
      const next: MapLight = {
        ...src,
        id,
        floor,
        position: [
          Number((state.snapMode === 'grid' ? Math.round(x / 0.1) * 0.1 : x).toFixed(3)),
          src.position[1],
          Number((state.snapMode === 'grid' ? Math.round(z / 0.1) * 0.1 : z).toFixed(3)),
        ],
        params: src.params ? { ...src.params } : undefined,
      };
      updatedArrays = {
        lights: [...state.lights, next],
      };
    } else if ('center' in src) {
      const next: MapTriggerVolume = {
        ...src,
        id,
        floor,
        center: [
          Number((state.snapMode === 'grid' ? Math.round(x / 0.1) * 0.1 : x).toFixed(3)),
          Number((state.snapMode === 'grid' ? Math.round(z / 0.1) * 0.1 : z).toFixed(3)),
        ],
        params: src.params ? { ...src.params } : undefined,
      };
      updatedArrays = {
        triggers: [...state.triggers, next],
      };
    } else if ('material' in src) {
      const next: MapDecal = {
        ...src,
        id,
        floor,
        position: [
          Number((state.snapMode === 'grid' ? Math.round(x / 0.05) * 0.05 : x).toFixed(3)),
          src.position[1],
          Number((state.snapMode === 'grid' ? Math.round(z / 0.05) * 0.05 : z).toFixed(3)),
        ],
        params: src.params ? { ...src.params } : undefined,
      };
      updatedArrays = {
        decals: [...state.decals, next],
      };
    } else if ('metadata' in src) {
      const next: MapRoomMarker = {
        ...src,
        id,
        floor,
        position: [
          Number((state.snapMode === 'grid' ? Math.round(x / 0.1) * 0.1 : x).toFixed(3)),
          Number((state.snapMode === 'grid' ? Math.round(z / 0.1) * 0.1 : z).toFixed(3)),
        ],
        metadata: src.metadata ? { ...src.metadata } : undefined,
      };
      updatedArrays = {
        markers: [...state.markers, next],
      };
    } else {
      return null;
    }


    set({
      ...undoPatch,
      ...updatedArrays,
      selectedObjectIds: [id],
      statusMessage: `Pasted object.`,
      saveState: 'idle',
    });
    return id;
  },

  setLastRaycastHit: (pos) => set({ lastRaycastHit: pos }),

  // ── Gizmo ──────────────────────────────────────────────────────────────────

  setGizmoMode: (mode) => set({ gizmoMode: mode }),

  // ── Free-cam ───────────────────────────────────────────────────────────────

  setFreeCamMode: (enabled) => set({
    freeCamMode: enabled,
    statusMessage: enabled ? 'Free-cam enabled - WASD + Space/Shift to fly.' : 'Free-cam disabled.',
  }),

  // ── Marquee ────────────────────────────────────────────────────────────────

  setMarqueeRect: (rect) => set({ marqueeRect: rect }),

  updateObject: (id, updater, label = 'Edit object') => {
    const state = get();
    const nextInstances = state.objectInstances.map((instance) => (
      instance.id === id ? updater(instance) : instance
    ));
    set({
      ...pushUndoPatch(state, getSnapshotData(state), label),
      objectInstances: nextInstances,
    });
  },

  moveObject: (id, position, label = 'Move object') => {
    const state = get();
    const updatedState = moveEntityInternal(state, id, position, state.snapMode);
    set({
      ...pushUndoPatch(state, getSnapshotData(state), label),
      ...updatedState,
    });
  },

  moveObjectImmediate: (id, position) => {
    const state = get();
    const updatedState = moveEntityInternal(state, id, position, state.snapMode);
    set({
      ...updatedState,
    });
  },

  moveGroupBy: (ids, delta, label = 'Move objects') => {
    const state = get();
    if (ids.length === 0) return;
    const undoPatch = pushUndoPatch(state, getSnapshotData(state), label);
    let nextState = { ...state };
    for (const id of ids) {
      const pos = getEntityPosition(nextState, id);
      if (!pos) continue;
      const nextPos: [number, number, number] = [
        pos[0] + delta[0],
        pos[1] + delta[1],
        pos[2] + delta[2],
      ];
      nextState = {
        ...nextState,
        ...moveEntityInternal(nextState, id, nextPos, state.snapMode),
      };
    }
    set({
      ...undoPatch,
      objectInstances: nextState.objectInstances,
      lights: nextState.lights,
      triggers: nextState.triggers,
      decals: nextState.decals,
      markers: nextState.markers,
    });
  },

  moveGroupImmediate: (ids, delta) => {
    const state = get();
    if (ids.length === 0) return;
    let nextState = { ...state };
    for (const id of ids) {
      const pos = getEntityPosition(nextState, id);
      if (!pos) continue;
      const nextPos: [number, number, number] = [
        pos[0] + delta[0],
        pos[1] + delta[1],
        pos[2] + delta[2],
      ];
      nextState = {
        ...nextState,
        ...moveEntityInternal(nextState, id, nextPos, state.snapMode),
      };
    }
    set({
      objectInstances: nextState.objectInstances,
      lights: nextState.lights,
      triggers: nextState.triggers,
      decals: nextState.decals,
      markers: nextState.markers,
    });
  },

  nudgeSelected: (delta) => {
    const state = get();
    if (state.selectedObjectIds.length === 0) return;
    get().moveGroupBy(state.selectedObjectIds, delta, 'Nudge');
  },

  beginDrag: (id, label = 'Move') => {
    const state = get();
    const ids = Array.isArray(id) ? id : [id];
    if (ids.length === 0) return;
    const hasAny = ids.some(id => getEntityCollection(state, id) !== null);
    if (!hasAny) return;
    set(pushUndoPatch(state, getSnapshotData(state), label));
  },

  rotateObject: (id, deltaY) => {
    const state = get();
    const updatedState = rotateEntityInternal(state, id, deltaY);
    set({
      ...pushUndoPatch(state, getSnapshotData(state), 'Rotate'),
      ...updatedState,
    });
  },

  updateObjectParam: (id, key, value) => {
    const state = get();
    const collection = getEntityCollection(state, id);
    if (!collection) return;

    let nextValue = value;
    if (collection === 'objectInstances') {
      const instance = state.objectInstances.find((i) => i.id === id);
      if (instance) {
        const schema = resolveParamSchema(instance.assetId, key);
        if (schema) {
          const coerced = coerceParamValue(schema, value);
          if (coerced === null) {
            set({
              saveState: 'error',
              statusMessage: `Invalid value for ${key}: ${JSON.stringify(value)}`,
            });
            return;
          }
          nextValue = coerced;
        }
      }
    }

    const updatedState = updateEntityParamInternal(state, id, key, nextValue);
    set({
      ...pushUndoPatch(state, getSnapshotData(state), `Edit ${key}`),
      ...updatedState,
    });
  },

  addObject: (assetId, floor, position, rotationY) => {
    const state = get();
    const definition = getObjectDefinition(assetId);
    if (!definition) {
      set({ saveState: 'error', statusMessage: `Unknown asset: ${assetId}` });
      return null;
    }

    const id = makeObjectId(assetId);
    const effectiveRotation = rotationY ?? state.placementRotationY;

    let updatedArrays: EntityArraysPatch;
    if (assetId.startsWith('light_')) {
      const newLight: MapLight = {
        id,
        floor,
        position: snapPosition(assetId, position, state.snapMode),
        ...lightFieldsFromAssetId(assetId, definition),
      };
      updatedArrays = { lights: [...state.lights, newLight] };
    } else if (assetId.startsWith('trigger_')) {
      const kind = assetId.replace('trigger_', '');
      const newTrigger: MapTriggerVolume = {
        id,
        floor,
        center: [
          Number((state.snapMode === 'grid' ? Math.round(position[0] / 0.1) * 0.1 : position[0]).toFixed(3)),
          Number((state.snapMode === 'grid' ? Math.round(position[2] / 0.1) * 0.1 : position[2]).toFixed(3)),
        ],
        size: [definition.bounds.size[0], definition.bounds.size[2]],
        height: definition.bounds.size[1],
        kind,
        targetFloor: definition.defaultParams?.targetFloor as number ?? 0,
        eventName: definition.defaultParams?.eventName as string ?? '',
        params: definition.defaultParams ? { ...definition.defaultParams } : {},
      };
      updatedArrays = { triggers: [...state.triggers, newTrigger] };
    } else if (assetId.startsWith('decal_')) {
      const material = assetId.replace('decal_', '');
      const newDecal: MapDecal = {
        id,
        floor,
        position: snapPosition(assetId, position, state.snapMode),
        rotation: [0, Number(effectiveRotation.toFixed(3)), 0],
        size: [definition.bounds.size[0], definition.bounds.size[2]],
        material,
        opacity: definition.defaultParams?.opacity as number ?? 1.0,
        params: definition.defaultParams ? { ...definition.defaultParams } : {},
      };
      updatedArrays = { decals: [...state.decals, newDecal] };
    } else if (assetId.startsWith('marker_')) {
      const kind = assetId.replace('marker_', '') as 'spawn' | 'checkpoint' | 'event' | 'custom';
      const newMarker: MapRoomMarker = {
        id,
        floor,
        position: [
          Number((state.snapMode === 'grid' ? Math.round(position[0] / 0.1) * 0.1 : position[0]).toFixed(3)),
          Number((state.snapMode === 'grid' ? Math.round(position[2] / 0.1) * 0.1 : position[2]).toFixed(3)),
        ],
        name: definition.name,
        kind,
        metadata: definition.defaultParams ? { ...definition.defaultParams } : {},
      };
      updatedArrays = { markers: [...state.markers, newMarker] };
    } else {
      const nextObject: MapObjectInstance = {
        id,
        assetId,
        floor,
        transform: {
          position: snapPosition(assetId, position, state.snapMode),
          rotation: [0, Number(effectiveRotation.toFixed(3)), 0],
          scale: [1, 1, 1],
        },
        params: definition.defaultParams ? { ...definition.defaultParams } : undefined,
        tags: definition.editor.tags ? [...definition.editor.tags] : undefined,
      };
      updatedArrays = { objectInstances: [...state.objectInstances, nextObject] };
    }


    set({
      ...pushUndoPatch(state, getSnapshotData(state), `Add ${definition.name}`),
      ...updatedArrays,
      selectedObjectIds: [id],
      pendingAssetId: null,
      placementPreview: null,
      placementRotationY: 0,
      statusMessage: `Placed ${definition.name}.`,
      saveState: 'idle',
    });

    return id;
  },

  insertInstance: (instance, label = 'Insert') => {
    const state = get();
    const nextInstances = [...state.objectInstances, instance];
    set({
      ...pushUndoPatch(state, getSnapshotData(state), label),
      objectInstances: nextInstances,
      selectedObjectIds: [instance.id],
      statusMessage: `Inserted ${instance.assetId}.`,
      saveState: 'idle',
    });
  },

  insertPrefabGroup: (group, floor, offset, label = 'Drop prefab') => {
    const state = get();
    const [dx, dz] = offset;
    const newIds: string[] = [];

    const newInstances: MapObjectInstance[] = (group.instances ?? []).map((source) => {
      const id = makeObjectId(source.assetId);
      newIds.push(id);
      return {
        ...source,
        id,
        floor,
        transform: {
          ...source.transform,
          position: [
            dx + source.transform.position[0],
            source.transform.position[1],
            dz + source.transform.position[2],
          ],
        },
        params: source.params ? { ...source.params } : undefined,
        tags: source.tags ? [...source.tags] : undefined,
      };
    });

    const newLights: MapLight[] = (group.lights ?? []).map((source) => {
      const id = makeObjectId(source.kind === 'spot' ? 'light_spot' : 'light_point');
      newIds.push(id);
      return {
        ...source,
        id,
        floor,
        position: [dx + source.position[0], source.position[1], dz + source.position[2]],
        params: source.params ? { ...source.params } : undefined,
      };
    });

    const newTriggers: MapTriggerVolume[] = (group.triggers ?? []).map((source) => {
      const id = makeObjectId(`trigger_${source.kind}`);
      newIds.push(id);
      return {
        ...source,
        id,
        floor,
        center: [dx + source.center[0], dz + source.center[1]],
        params: source.params ? { ...source.params } : undefined,
      };
    });

    const newDecals: MapDecal[] = (group.decals ?? []).map((source) => {
      const id = makeObjectId(`decal_${source.material}`);
      newIds.push(id);
      return {
        ...source,
        id,
        floor,
        position: [dx + source.position[0], source.position[1], dz + source.position[2]],
        rotation: source.rotation ? [...source.rotation] as [number, number, number] : undefined,
        params: source.params ? { ...source.params } : undefined,
      };
    });

    set({
      ...pushUndoPatch(state, getSnapshotData(state), label),
      objectInstances: [...state.objectInstances, ...newInstances],
      lights: [...state.lights, ...newLights],
      triggers: [...state.triggers, ...newTriggers],
      decals: [...state.decals, ...newDecals],
      selectedObjectIds: newIds,
      statusMessage: `Inserted ${newIds.length} object${newIds.length === 1 ? '' : 's'}.`,
      saveState: 'idle',
    });

    return newIds;
  },

  duplicateObject: (id) => {
    const state = get();
    const collection = getEntityCollection(state, id);
    if (!collection) return null;
    const newIds = get().duplicateWithOffset(id, 1, [0.6, 0.6]);
    return newIds[0] ?? null;
  },

  duplicateWithOffset: (id, count, offset) => {
    const state = get();
    const collection = getEntityCollection(state, id);
    if (!collection || count < 1) return [];

    const undoPatch = pushUndoPatch(
      state,
      getSnapshotData(state),
      count > 1 ? `Duplicate ${count}×` : 'Duplicate',
    );

    let updatedArrays: EntityArraysPatch = {};
    const newIds: string[] = [];

    if (collection === 'objectInstances') {
      const source = state.objectInstances.find(i => i.id === id)!;
      const newInstances: MapObjectInstance[] = [];
      for (let i = 1; i <= count; i += 1) {
        const newId = makeObjectId(source.assetId);
        newIds.push(newId);
        const nextPosition: [number, number, number] = [
          source.transform.position[0] + offset[0] * i,
          source.transform.position[1],
          source.transform.position[2] + offset[1] * i,
        ];
        newInstances.push({
          ...source,
          id: newId,
          transform: {
            ...source.transform,
            position: snapPosition(source.assetId, nextPosition, state.snapMode),
            rotation: source.transform.rotation ? [...source.transform.rotation] as [number, number, number] : [0, 0, 0],
            scale: source.transform.scale ? [...source.transform.scale] as [number, number, number] : [1, 1, 1],
          },
          params: source.params ? { ...source.params } : undefined,
          tags: source.tags ? [...source.tags] : undefined,
        });
      }
      updatedArrays = { objectInstances: [...state.objectInstances, ...newInstances] };
    } else if (collection === 'lights') {
      const source = state.lights.find(l => l.id === id)!;
      const newLights: MapLight[] = [];
      for (let i = 1; i <= count; i += 1) {
        const newId = makeObjectId(source.kind === 'spot' ? 'light_spot' : 'light_point');
        newIds.push(newId);
        const nextPosition: [number, number, number] = [
          Number((source.position[0] + offset[0] * i).toFixed(3)),
          source.position[1],
          Number((source.position[2] + offset[1] * i).toFixed(3)),
        ];
        newLights.push({
          ...source,
          id: newId,
          position: nextPosition,
          params: source.params ? { ...source.params } : undefined,
        });
      }
      updatedArrays = { lights: [...state.lights, ...newLights] };
    } else if (collection === 'triggers') {
      const source = state.triggers.find(t => t.id === id)!;
      const newTriggers: MapTriggerVolume[] = [];
      for (let i = 1; i <= count; i += 1) {
        const newId = makeObjectId(`trigger_${source.kind}`);
        newIds.push(newId);
        const nextCenter: [number, number] = [
          Number((source.center[0] + offset[0] * i).toFixed(3)),
          Number((source.center[1] + offset[1] * i).toFixed(3)),
        ];
        newTriggers.push({
          ...source,
          id: newId,
          center: nextCenter,
          params: source.params ? { ...source.params } : undefined,
        });
      }
      updatedArrays = { triggers: [...state.triggers, ...newTriggers] };
    } else if (collection === 'decals') {
      const source = state.decals.find(d => d.id === id)!;
      const newDecals: MapDecal[] = [];
      for (let i = 1; i <= count; i += 1) {
        const newId = makeObjectId(`decal_${source.material}`);
        newIds.push(newId);
        const nextPosition: [number, number, number] = [
          Number((source.position[0] + offset[0] * i).toFixed(3)),
          source.position[1],
          Number((source.position[2] + offset[1] * i).toFixed(3)),
        ];
        newDecals.push({
          ...source,
          id: newId,
          position: nextPosition,
          params: source.params ? { ...source.params } : undefined,
        });
      }
      updatedArrays = { decals: [...state.decals, ...newDecals] };
    } else if (collection === 'markers') {
      const source = state.markers.find(m => m.id === id)!;
      const newMarkers: MapRoomMarker[] = [];
      for (let i = 1; i <= count; i += 1) {
        const newId = makeObjectId(`marker_${source.kind}`);
        newIds.push(newId);
        const nextPosition: [number, number] = [
          Number((source.position[0] + offset[0] * i).toFixed(3)),
          Number((source.position[1] + offset[1] * i).toFixed(3)),
        ];
        newMarkers.push({
          ...source,
          id: newId,
          position: nextPosition,
          metadata: source.metadata ? { ...source.metadata } : undefined,
        });
      }
      updatedArrays = { markers: [...state.markers, ...newMarkers] };
    }


    set({
      ...undoPatch,
      ...updatedArrays,
      selectedObjectIds: newIds,
      statusMessage: `Duplicated ${count} object${count > 1 ? 's' : ''}.`,
      saveState: 'idle',
    });

    return newIds;
  },

  deleteObject: (id) => {
    const state = get();
    const updatedState = deleteEntityInternal(state, id);
    set({
      ...pushUndoPatch(state, getSnapshotData(state), 'Delete'),
      ...updatedState,
      saveState: 'idle',
    });
  },

  deleteSelected: () => {
    const state = get();
    if (state.selectedObjectIds.length === 0) return;
    const ids = new Set(state.selectedObjectIds);
    const undoPatch = pushUndoPatch(
      state,
      getSnapshotData(state),
      ids.size > 1 ? `Delete ${ids.size} objects` : 'Delete',
    );
    
    const nextInstances = state.objectInstances.filter((instance) => !ids.has(instance.id));
    const nextLights = state.lights.filter((l) => !ids.has(l.id));
    const nextTriggers = state.triggers.filter((t) => !ids.has(t.id));
    const nextDecals = state.decals.filter((d) => !ids.has(d.id));
    const nextMarkers = state.markers.filter((m) => !ids.has(m.id));
    
    set({
      ...undoPatch,
      objectInstances: nextInstances,
      lights: nextLights,
      triggers: nextTriggers,
      decals: nextDecals,
      markers: nextMarkers,
      selectedObjectIds: [],
      statusMessage: `Removed ${ids.size} object${ids.size > 1 ? 's' : ''}.`,
      saveState: 'idle',
    });
  },

  undo: () => {
    const state = get();
    if (state.undoStack.length === 0) return;
    const [previous, ...rest] = state.undoStack as [UndoEntry, ...UndoEntry[]];
    const currentSnapshot = getSnapshotData(state);
    const prevSnapshot = cloneSnapshotData(previous.snapshot);
    set({
      objectInstances: prevSnapshot.instances,
      lights: prevSnapshot.lights,
      triggers: prevSnapshot.triggers,
      decals: prevSnapshot.decals,
      markers: prevSnapshot.markers,
      undoStack: rest,
      redoStack: [{ snapshot: currentSnapshot, label: previous.label }, ...state.redoStack].slice(0, UNDO_LIMIT),
      selectedObjectIds: [],
      statusMessage: `Undo: ${previous.label}`,
      saveState: 'idle',
    });
  },

  redo: () => {
    const state = get();
    if (state.redoStack.length === 0) return;
    const [next, ...rest] = state.redoStack as [UndoEntry, ...UndoEntry[]];
    const currentSnapshot = getSnapshotData(state);
    const nextSnapshot = cloneSnapshotData(next.snapshot);
    set({
      objectInstances: nextSnapshot.instances,
      lights: nextSnapshot.lights,
      triggers: nextSnapshot.triggers,
      decals: nextSnapshot.decals,
      markers: nextSnapshot.markers,
      redoStack: rest,
      undoStack: [{ snapshot: currentSnapshot, label: next.label }, ...state.undoStack].slice(0, UNDO_LIMIT),
      selectedObjectIds: [],
      statusMessage: `Redo: ${next.label}`,
      saveState: 'idle',
    });
  },

  resetToSource: () =>
    set((state) => ({
      objectInstances: cloneInstances(state.sourceInstances),
      lights: state.sourceLights.map(cloneLight),
      triggers: state.sourceTriggers.map(cloneTrigger),
      decals: state.sourceDecals.map(cloneDecal),
      markers: state.sourceMarkers.map(cloneMarker),
      selectedObjectIds: [],
      pendingAssetId: null,
      placementPreview: null,
      undoStack: [],
      redoStack: [],
      statusMessage: 'Reverted unsaved editor changes.',
      saveState: 'idle',
      lastValidationIssues: [],
    })),

  restoreFromSnapshot: (instances, label) => {
    const state = get();
    set({
      ...pushUndoPatch(state, getSnapshotData(state), label),
      objectInstances: cloneInstances(instances),
      selectedObjectIds: [],
      pendingAssetId: null,
      placementPreview: null,
      statusMessage: label,
      saveState: 'idle',
    });
  },

  loadFromDisk: async () => {
    set({ saveState: 'saving', statusMessage: 'Loading saved map content...' });
    try {
      const response = await getMapPersistence().loadMap(getActiveProjectId());
      const docLights = response.lights ? response.lights.map(cloneLight) : [];
      const docTriggers = response.triggers ? response.triggers.map(cloneTrigger) : [];
      const docDecals = response.decals ? response.decals.map(cloneDecal) : [];
      const docMarkers = response.markers ? response.markers.map(cloneMarker) : [];

      set({
        objectInstances: cloneInstances(response.objectInstances),
        sourceInstances: cloneInstances(response.objectInstances),
        lights: docLights,
        sourceLights: response.lights ? response.lights.map(cloneLight) : [],
        triggers: docTriggers,
        sourceTriggers: response.triggers ? response.triggers.map(cloneTrigger) : [],
        decals: docDecals,
        sourceDecals: response.decals ? response.decals.map(cloneDecal) : [],
        markers: docMarkers,
        sourceMarkers: response.markers ? response.markers.map(cloneMarker) : [],
        selectedObjectIds: [],
        pendingAssetId: null,
        placementPreview: null,
        undoStack: [],
        redoStack: [],
        saveState: 'saved',
        statusMessage: 'Reloaded saved map content.',
        lastValidationIssues: [],
      });
    } catch (error) {
      set({
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to load map content.',
      });
    }
  },

  saveToDisk: async (options) => {
    const state = get();
    const issues = validateInstances(state.objectInstances);
    const blocking = issues.filter((i) => i.severity === 'error');

    // Structural lint over the LIVE document (markers/triggers/etc), not the
    // frozen hydration snapshot. Sunder completeness failures are hard errors:
    // the sim rejects such maps at load, so an explicit save is blocked before
    // publish. Autosave is exempt - it backs up work-in-progress to a transient
    // slot, so a temporarily-incomplete map must never lose its backup.
    const liveDoc = buildLiveMapDocument(state);
    const lintIssues = liveDoc ? lintMap(liveDoc, state.objectInstances) : [];
    const lintErrors = options?.autosave ? [] : lintIssues.filter((i) => i.severity === 'error');

    if ((blocking.length > 0 || lintErrors.length > 0) && !options?.skipValidation) {
      const parts: string[] = [];
      if (blocking.length > 0) {
        parts.push(`${blocking.length} validation error${blocking.length > 1 ? 's' : ''}`);
      }
      if (lintErrors.length > 0) {
        parts.push(`${lintErrors.length} map error${lintErrors.length > 1 ? 's' : ''}: ${lintErrors[0]!.message}`);
      }
      set({
        saveState: 'error',
        statusMessage: `Cannot save: ${parts.join('; ')}`,
        lastValidationIssues: issues,
        lastLintIssues: lintIssues,
      });
      return issues;
    }
    set({
      saveState: 'saving',
      statusMessage: options?.autosave ? 'Auto-saving…' : 'Saving map content…',
      lastLintIssues: lintIssues,
    });
    try {
      const response = await getMapPersistence().saveMap(
        getActiveProjectId(),
        state.objectInstances,
        options,
        {
          lights: state.lights,
          triggers: state.triggers,
          decals: state.decals,
          markers: state.markers,
        },
      );
      
      const docLights = response.lights ? response.lights.map(cloneLight) : [];
      const docTriggers = response.triggers ? response.triggers.map(cloneTrigger) : [];
      const docDecals = response.decals ? response.decals.map(cloneDecal) : [];
      const docMarkers = response.markers ? response.markers.map(cloneMarker) : [];

      set({
        objectInstances: cloneInstances(response.objectInstances),
        sourceInstances: options?.autosave ? state.sourceInstances : cloneInstances(response.objectInstances),
        lights: docLights,
        sourceLights: options?.autosave ? state.sourceLights : response.lights ? response.lights.map(cloneLight) : [],
        triggers: docTriggers,
        sourceTriggers: options?.autosave ? state.sourceTriggers : response.triggers ? response.triggers.map(cloneTrigger) : [],
        decals: docDecals,
        sourceDecals: options?.autosave ? state.sourceDecals : response.decals ? response.decals.map(cloneDecal) : [],
        markers: docMarkers,
        sourceMarkers: options?.autosave ? state.sourceMarkers : response.markers ? response.markers.map(cloneMarker) : [],
        undoStack: options?.autosave ? state.undoStack : [],
        redoStack: options?.autosave ? state.redoStack : [],
        saveState: options?.autosave ? 'autosaved' : 'saved',
        statusMessage: options?.autosave ? 'Auto-saved to backup.' : 'Saved to source file.',
        lastValidationIssues: issues,
      });
      return issues;
    } catch (error) {
      set({
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to save map content.',
      });
      return issues;
    }
  },

  loadGitHeadSnapshot: async () => {
    try {
      const response = await getMapPersistence().loadSnapshot(getActiveProjectId());
      return response.objectInstances;
    } catch (error) {
      set({
        saveState: 'error',
        statusMessage: error instanceof Error ? error.message : 'Failed to read git HEAD.',
      });
      return null;
    }
  },

  setAutoSaveEnabled: (enabled) => {
    set({
      autoSaveEnabled: enabled,
      statusMessage: enabled ? 'Auto-save enabled (writes to .autosave.bak).' : 'Auto-save disabled.',
    });
    saveEditorPreferences({
      snapMode: get().snapMode,
      autoSaveEnabled: enabled,
      activeRightPanel: get().activeRightPanel,
    });
  },

  // Only the transient one-shot message, never saveState: that field is the
  // top bar's persistent "Saved" / "Backed up" / "Save failed" indicator and
  // must keep reflecting the last real save outcome after the toast forwarding
  // it has been dismissed.
  clearStatus: () => set({ statusMessage: null }),

  validateAll: () => {
    const issues = validateInstances(get().objectInstances);
    set({ lastValidationIssues: issues });
    return issues;
  },

  focusOnInstance: (id, options) => {
    const state = get();
    const collection = getEntityCollection(state, id);
    if (!collection) return;

    let pos: [number, number, number] = [0, 0, 0];
    let halfHeight = 0.8;
    let name = 'object';

    if (collection === 'objectInstances') {
      const instance = state.objectInstances.find((i) => i.id === id)!;
      pos = instance.transform.position;
      const definition = getObjectDefinition(instance.assetId);
      halfHeight = definition ? definition.bounds.size[1] / 2 : 0.8;
      name = definition ? definition.name : 'object';
    } else if (collection === 'lights') {
      const light = state.lights.find((l) => l.id === id)!;
      pos = light.position;
      name = light.kind === 'spot' ? 'Spot Light' : 'Point Light';
    } else if (collection === 'decals') {
      const decal = state.decals.find((d) => d.id === id)!;
      pos = decal.position;
      name = `Decal (${decal.material})`;
    } else if (collection === 'triggers') {
      const trigger = state.triggers.find((t) => t.id === id)!;
      pos = [trigger.center[0], 0, trigger.center[1]];
      name = `Trigger (${trigger.kind})`;
      halfHeight = trigger.height / 2;
    } else if (collection === 'markers') {
      const marker = state.markers.find((m) => m.id === id)!;
      pos = [marker.position[0], 0, marker.position[1]];
      name = `Marker (${marker.name})`;
    }

    const [ox, oy, oz] = pos;
    const lookY = oy + halfHeight;
    const approach: [number, number, number] = [ox, 0, oz + 3];
    const lookAt: [number, number, number] = [ox, lookY, oz];

    let floor = 0;
    if (collection === 'objectInstances') floor = state.objectInstances.find(i => i.id === id)!.floor;
    else if (collection === 'lights') floor = state.lights.find(l => l.id === id)!.floor;
    else if (collection === 'triggers') floor = state.triggers.find(t => t.id === id)!.floor;
    else if (collection === 'decals') floor = state.decals.find(d => d.id === id)!.floor;
    else if (collection === 'markers') floor = state.markers.find(m => m.id === id)!.floor;

    const fromFloor = options?.currentFloor ?? floor;
    const instant = fromFloor !== floor;

    set({
      focusTarget: {
        instanceId: id,
        floor,
        approach,
        lookAt,
        instant,
      },
      selectedObjectIds: [id],
      pendingAssetId: null,
      statusMessage: `Framing ${name}…`,
      saveState: 'idle',
    });
  },

  clearFocusTarget: () => set({ focusTarget: null }),
}));
